import { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import { DESIGN_TOKENS } from '../styles/theme';
import { SectionHeader } from '../components/common';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 200;
const GROUND_Y = 170;
const TRUCK_WIDTH = 60;
const TRUCK_HEIGHT = 40;
const GRAVITY = 0.6;
const JUMP_STRENGTH = -12;
const INITIAL_OBSTACLE_SPEED = 6;
const OBSTACLE_ACCELERATION = 0.001;

interface Obstacle {
  x: number;
  width: number;
  height: number;
  type: 'cone' | 'barrier';
}

export default function DinosaurGamePage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('dino_high_score') || '0', 10);
  });

  const gameState = useRef({
    truckY: GROUND_Y - TRUCK_HEIGHT,
    truckVelocityY: 0,
    isJumping: false,
    obstacles: [] as Obstacle[],
    obstacleSpeed: INITIAL_OBSTACLE_SPEED,
    frameCount: 0,
    lastObstacleFrame: 0,
    currentScore: 0,
  });

  const resetGame = () => {
    gameState.current = {
      truckY: GROUND_Y - TRUCK_HEIGHT,
      truckVelocityY: 0,
      isJumping: false,
      obstacles: [],
      obstacleSpeed: INITIAL_OBSTACLE_SPEED,
      frameCount: 0,
      lastObstacleFrame: 0,
      currentScore: 0,
    };
    setScore(0);
    setIsGameOver(false);
    setIsGameStarted(true);
  };

  const jump = () => {
    if (!gameState.current.isJumping && isGameStarted && !isGameOver) {
      gameState.current.truckVelocityY = JUMP_STRENGTH;
      gameState.current.isJumping = true;
    } else if (!isGameStarted || isGameOver) {
      resetGame();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        jump();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGameStarted, isGameOver]);

  useEffect(() => {
    if (!isGameStarted || isGameOver) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;

    const gameLoop = () => {
      // Update Game State
      gameState.current.frameCount++;
      gameState.current.currentScore += 0.1;
      setScore(gameState.current.currentScore);
      gameState.current.obstacleSpeed += OBSTACLE_ACCELERATION;

      // Physics
      gameState.current.truckY += gameState.current.truckVelocityY;
      gameState.current.truckVelocityY += GRAVITY;

      if (gameState.current.truckY > GROUND_Y - TRUCK_HEIGHT) {
        gameState.current.truckY = GROUND_Y - TRUCK_HEIGHT;
        gameState.current.truckVelocityY = 0;
        gameState.current.isJumping = false;
      }

      // Obstacle Spawning
      const minGap = 50 + gameState.current.obstacleSpeed * 10;
      if (gameState.current.frameCount - gameState.current.lastObstacleFrame > Math.max(60, minGap / gameState.current.obstacleSpeed)) {
        if (Math.random() < 0.02) {
          const type = Math.random() > 0.5 ? 'cone' : 'barrier';
          gameState.current.obstacles.push({
            x: GAME_WIDTH,
            width: type === 'cone' ? 20 : 40,
            height: type === 'cone' ? 30 : 25,
            type,
          });
          gameState.current.lastObstacleFrame = gameState.current.frameCount;
        }
      }

      // Move Obstacles & Collision Detection
      gameState.current.obstacles = gameState.current.obstacles.filter(obs => {
        obs.x -= gameState.current.obstacleSpeed;

        // Collision Check
        const truckBox = {
          x: 50,
          y: gameState.current.truckY,
          width: TRUCK_WIDTH,
          height: TRUCK_HEIGHT,
        };

        if (
          truckBox.x < obs.x + obs.width &&
          truckBox.x + truckBox.width > obs.x &&
          truckBox.y < GROUND_Y && // Simple check for grounded
          truckBox.y + truckBox.height > GROUND_Y - obs.height
        ) {
          setIsGameOver(true);
          if (gameState.current.currentScore > highScore) {
            setHighScore(Math.floor(gameState.current.currentScore));
            localStorage.setItem('dino_high_score', Math.floor(gameState.current.currentScore).toString());
          }
        }

        return obs.x > -obs.width;
      });

      // Drawing
      ctx.clearRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

      // Draw Ground
      ctx.strokeStyle = DESIGN_TOKENS.colors.textPrimary;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y);
      ctx.lineTo(GAME_WIDTH, GROUND_Y);
      ctx.stroke();

      // Draw Truck (simplified)
      ctx.fillStyle = DESIGN_TOKENS.colors.primary;
      // Cabin
      ctx.fillRect(50 + 40, gameState.current.truckY, 20, 25);
      // Body
      ctx.fillStyle = DESIGN_TOKENS.colors.slate;
      ctx.fillRect(50, gameState.current.truckY + 10, 45, 20);
      // Wheels
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.arc(50 + 10, gameState.current.truckY + 35, 5, 0, Math.PI * 2);
      ctx.arc(50 + 35, gameState.current.truckY + 35, 5, 0, Math.PI * 2);
      ctx.arc(50 + 52, gameState.current.truckY + 35, 5, 0, Math.PI * 2);
      ctx.fill();

      // Draw Obstacles
      gameState.current.obstacles.forEach(obs => {
        if (obs.type === 'cone') {
          ctx.fillStyle = '#FF6B00';
          ctx.beginPath();
          ctx.moveTo(obs.x, GROUND_Y);
          ctx.lineTo(obs.x + obs.width / 2, GROUND_Y - obs.height);
          ctx.lineTo(obs.x + obs.width, GROUND_Y);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.fillStyle = '#555';
          ctx.fillRect(obs.x, GROUND_Y - obs.height, obs.width, obs.height);
          ctx.fillStyle = '#fff';
          ctx.fillRect(obs.x + 5, GROUND_Y - obs.height + 5, obs.width - 10, 5);
        }
      });

      if (!isGameOver) {
        animationFrameId = requestAnimationFrame(gameLoop);
      }
    };

    animationFrameId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isGameStarted, isGameOver, highScore]);

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '80vh', py: 8 }}>
      <Container maxWidth="md">
        <SectionHeader
          title="Dino Trucker"
          subtitle="Omiń przeszkody i dojedź jak najdalej! Naciśnij spację, aby skoczyć."
          centered
        />

        <Paper
          elevation={0}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: DESIGN_TOKENS.colors.surface,
            borderRadius: DESIGN_TOKENS.borderRadius.card,
            border: `1px solid ${DESIGN_TOKENS.colors.border}`,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              Wynik: {Math.floor(score)}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="text.secondary">
              Najlepszy wynik: {highScore}
            </Typography>
          </Box>

          <Box
            onClick={jump}
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: GAME_WIDTH,
              height: GAME_HEIGHT,
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              borderBottom: `2px solid ${DESIGN_TOKENS.colors.textPrimary}`,
              overflow: 'hidden',
              cursor: 'pointer',
              touchAction: 'none'
            }}
          >
            <canvas
              ref={canvasRef}
              width={GAME_WIDTH}
              height={GAME_HEIGHT}
              style={{ width: '100%', height: '100%' }}
            />

            {!isGameStarted && !isGameOver && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 2
                }}
              >
                <Button variant="contained" size="large" onClick={(e) => { e.stopPropagation(); resetGame(); }}>
                  Zacznij grę
                </Button>
              </Box>
            )}

            {isGameOver && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  zIndex: 2
                }}
              >
                <Typography variant="h4" fontWeight={900} gutterBottom>
                  KONIEC GRY
                </Typography>
                <Button variant="contained" color="secondary" onClick={(e) => { e.stopPropagation(); resetGame(); }}>
                  Zagraj ponownie
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Użyj <strong>SPACJI</strong> lub <strong>KLIKNIJ</strong> w obszar gry, aby skoczyć.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
